import {
  Box,
  Flex,
  Table,
  Td,
  Th,
  Thead,
  Tr,
  Tbody,
  Text,
  InputGroup,
  Input,
  Checkbox,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import {
  CreditAgreement,
  typeguards as finleyTypeguards,
} from '@finley-technologies/finley-types';

import { useDraggableRow } from 'packages/common-frontend/components/DragAndDropTable/hooks';
import * as DragAndDropTableTypes from 'packages/common-frontend/components/DragAndDropTable/types';
import { TrDraggableHandle } from 'packages/common-frontend/components/DragAndDropTable/ui';
import {
  AddIconButton,
  EditIconButton,
  DeleteIconButton,
} from 'packages/common-frontend/components/IconButton';
import { getIterationKey } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { WipConfig } from 'packages/creditAgreements/ui/types';

type QueryVariable = CreditAgreement.Config.QueryVariable;

type EditVariableModalProps = {
  targetVariable: QueryVariable;
  variables: QueryVariable[];
  isOpen: boolean;
  onCancel: () => void;
  onSave: (newVariable: QueryVariable) => void;
};
const EditVariableModal = ({
  targetVariable,
  variables,
  isOpen,
  onCancel,
  onSave,
}: EditVariableModalProps) => {
  const [name, setName] = useState<string>(targetVariable.name);
  const [parseNumber, setParseNumber] = useState<boolean>(
    targetVariable.parseNumber,
  );
  const [value, setValue] = useState<string>(targetVariable.value);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const onValidateAndSave = (): void => {
    setErrorMessage('');

    const errors: string[] = [];
    if (name.length === 0) {
      errors.push('Can not have empty value');
    } else {
      const existingVariables = variables.filter(
        (v) => v.name.toLowerCase() === name.toLowerCase(),
      );
      if (existingVariables.length > 1) {
        errors.push(`Can not have duplicate name: ${name}`);
      }
    }

    if (value.length === 0) {
      errors.push('Can not have empty value');
    }

    if (errors.length > 0) {
      setErrorMessage(errors.join(', '));
      return;
    }

    const newVariable: QueryVariable = {
      name: name.trim(),
      value: value.trim(),
      parseNumber,
    };
    if (!finleyTypeguards.creditAgreement.isQueryVariable(newVariable)) {
      setErrorMessage('Detected unsupported Query Variable structure');
      return;
    }

    onSave(newVariable);
    onCancel();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onCancel();
      }}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent p="2">
        <ModalHeader pb="1">Edit Athena Variable</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Flex alignItems="center" my="6">
            <Text w="32" mr="2" fontWeight="bold">
              Name:
            </Text>

            <InputGroup w="72">
              <Input
                type="text"
                value={name}
                onChange={(evt) => setName(evt.target.value)}
              />
            </InputGroup>
          </Flex>

          <Flex alignItems="center" mb="6">
            <Text w="32" mr="2" fontWeight="bold">
              Parse String:
            </Text>
            <Checkbox
              isChecked={parseNumber}
              onChange={(e) => setParseNumber(e.target.checked)}
            />
          </Flex>

          <Box mb="6">
            <Text fontWeight="bold" mb="1">
              Variable Value
            </Text>
            <Text fontStyle="italic" fontSize="sm" mb="3">
              Values starting with &quot;=&quot; will be evaluated as formula
            </Text>
            <Editor
              value={value}
              onChange={(val) => setValue(val ?? '')}
              theme="vs-dark"
              width="100%"
              height="100px"
            />
          </Box>

          {errorMessage && (
            <Alert status="error" mb="7" rounded="md">
              <AlertIcon />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          <Flex justifyContent="center">
            <Button type="button" mr="5" onClick={() => onValidateAndSave()}>
              Update
            </Button>
            <Button onClick={() => onCancel()}>Close</Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type VariableViewProps = {
  wipConfig?: WipConfig;
  targetVariable: QueryVariable;
  variables: QueryVariable[];
  variablePropertyKey: string;
  idx: number;
  moveRow: DragAndDropTableTypes.MoveRow;
  isDragDisabled: boolean;
  id: string;
};
const VariableView = ({
  targetVariable,
  variables,
  variablePropertyKey,
  wipConfig,
  idx,
  moveRow,
  isDragDisabled,
  id,
}: VariableViewProps) => {
  const variableIdxPropertyKey = `${variablePropertyKey}[${idx}]`;

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  const onSave = (newVariable: QueryVariable): void => {
    if (!wipConfig?.isMutateAllowed(variablePropertyKey)) {
      return;
    }

    wipConfig.formProps.setFieldValue(variableIdxPropertyKey, newVariable);
  };

  const deleteVariable = (): void => {
    if (!wipConfig?.isMutateAllowed(variablePropertyKey)) {
      return;
    }

    const wipVariables = getIn(wipConfig.formProps.values, variablePropertyKey);
    finleyAssert(
      finleyTypeguards.creditAgreement.isQueryVariables(wipVariables),
      `Retrieved non-Athena Query Variables structure for [key:${variablePropertyKey}]`,
    );

    wipConfig.formProps.setFieldValue(
      variablePropertyKey,
      wipVariables.filter((_unused, loopIdx) => loopIdx !== idx),
    );
  };

  return (
    <TrDraggableHandle
      index={idx}
      id={id}
      acceptedType="queryVariable"
      moveRow={moveRow}
      isDragDisabled={isDragDisabled}
    >
      <Td>{targetVariable.name}</Td>
      <Td>{String(targetVariable.parseNumber).toUpperCase()}</Td>
      <Td>{targetVariable.value}</Td>

      {wipConfig?.isMutateAllowed(variableIdxPropertyKey) && isDragDisabled && (
        <Td>
          <EditIconButton
            ariaLabel="edit variable"
            label="Edit Variable"
            onClick={() => setIsEditModalOpen(true)}
          />
          <DeleteIconButton
            ariaLabel="delete variable"
            label="Delete Variable"
            onClick={() => {
              deleteVariable();
            }}
          />
        </Td>
      )}

      {isEditModalOpen && (
        <EditVariableModal
          targetVariable={targetVariable}
          variables={variables}
          isOpen={isEditModalOpen}
          onCancel={() => setIsEditModalOpen(false)}
          onSave={onSave}
        />
      )}
    </TrDraggableHandle>
  );
};

type VariableContainerProps = {
  wipConfig?: WipConfig;
  variables: QueryVariable[];
  variablePropertyKey: string;
  subTitle?: string;
};
export const VariableContainer = ({
  variables,
  variablePropertyKey,
  wipConfig,
  subTitle,
}: VariableContainerProps) => {
  const {
    useRowState,
    useOrderLockedState,
    moveRow,
    ui: { LockOrderButton, RestoreOrderButton, SaveOrderCallout },
  } = useDraggableRow<QueryVariable>({ comparisonKey: 'name' });
  const [variablesState] = useRowState(variables);
  const [isOrderLocked] = useOrderLockedState();

  const addVariable = (): void => {
    if (!wipConfig?.isMutateAllowed(variablePropertyKey)) {
      return;
    }

    const wipVariables = getIn(wipConfig.formProps.values, variablePropertyKey);
    finleyAssert(
      finleyTypeguards.creditAgreement.isQueryVariables(wipVariables),
      `Retrieved non-Athena Query Variables structure for [key:${variablePropertyKey}]`,
    );

    const variablesCopy = _.cloneDeep(wipVariables);
    variablesCopy.push({
      name: `var${variablesCopy.length + 1}`,
      value: 'VALUE',
      parseNumber: true,
    });
    wipConfig.formProps.setFieldValue(variablePropertyKey, variablesCopy);
  };

  const onSaveOrder = (): void => {
    if (!wipConfig?.isMutateAllowed(variablePropertyKey)) {
      return;
    }

    wipConfig.formProps.setFieldValue(variablePropertyKey, variablesState);
  };

  return (
    <Box id="variable-full-container" mb="5">
      <Flex alignItems="center">
        <Text fontWeight="bold" mr="2">
          Variables
        </Text>

        {wipConfig?.isMutateAllowed(variablePropertyKey) && (
          <>
            {isOrderLocked && (
              <AddIconButton
                ariaLabel="add variable"
                label="Add Variable"
                onClick={() => {
                  addVariable();
                }}
              />
            )}
            {variables.length > 0 && (
              <LockOrderButton onSaveOrder={onSaveOrder} />
            )}
            {!isOrderLocked && <RestoreOrderButton />}
          </>
        )}
      </Flex>

      {wipConfig?.isMutateAllowed(variablePropertyKey) && <SaveOrderCallout />}

      {wipConfig?.isMutateAllowed(variablePropertyKey) && !!subTitle && (
        <Text fontStyle="italic" fontSize="sm" mb="3">
          {subTitle}
        </Text>
      )}

      {variables.length > 0 ? (
        <Table borderWidth="1px">
          <Thead>
            <Tr>
              {wipConfig && !isOrderLocked && <Th w="10" />}
              <Th w="96">Name</Th>
              <Th w="40">Parse</Th>
              <Th>Value</Th>
              {wipConfig && isOrderLocked && <Th w="40">Edit/Delete</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {variablesState.map((variable, idx) => {
              const id = getIterationKey(variable);
              return (
                <VariableView
                  key={id}
                  wipConfig={wipConfig}
                  targetVariable={variable}
                  variables={variables}
                  variablePropertyKey={variablePropertyKey}
                  idx={idx}
                  moveRow={moveRow}
                  isDragDisabled={isOrderLocked}
                  id={id}
                />
              );
            })}
          </Tbody>
        </Table>
      ) : (
        <Text p="3" border="1px" borderColor="gray.700">
          There are currently no variables configured
        </Text>
      )}
    </Box>
  );
};
